import { DateTime } from "luxon";

import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@src/store";

import {
  ExperienceListFiltersType,
  initialExperienceListFilters,
  initialPersonalizationListFilters,
} from "../../types/experience";
import {
  IAppSettingsState,
  LoggedOrg,
  PerformanceView,
} from "../types/appSettings";

const initialAppSettingsState: IAppSettingsState = {
  isShopLogin: true,
  loggedOrgs: [],
  dismissedAlerts: {},
  showDuplicateProductIntro: true,
  rememberEmail: undefined,
  onboardingPage: 0,
  showIds: false,
  snoozeShopifyScopesDialog: undefined,
  performanceViews: [{ dataKey: "TBT" }],
  appPadding: true,
  sideNavCollapsed: false,
  experienceListFilters: initialExperienceListFilters,
  personalizationListFilters: initialPersonalizationListFilters,
  viewAsMode: false,
  viewAsProductId: undefined,
};

const appSettingsSlice = rtk.createSlice({
  name: "appSettings",
  initialState: initialAppSettingsState,
  reducers: {
    setViewAsMode: (appSettings, value: PayloadAction<boolean>) => {
      appSettings.viewAsMode = value.payload;
    },
    setViewAsProductId: (
      appSettings,
      value: PayloadAction<string | undefined>
    ) => {
      appSettings.viewAsProductId = value.payload;
    },
    setShopLogin: (appSettings) => {
      appSettings.isShopLogin = true;
    },
    setEmailLogin: (appSettings) => {
      appSettings.isShopLogin = false;
    },
    toggleShowIds: (appSettings) => {
      appSettings.showIds = !appSettings.showIds;
    },
    disabledDuplicateProductIntro: (appSettings) => {
      appSettings.showDuplicateProductIntro = false;
    },
    rememberEmail: (appSettings, value: PayloadAction<string>) => {
      appSettings.rememberEmail = value.payload;
    },
    clearRememberedEmail: (appSettings) => {
      appSettings.rememberEmail = undefined;
    },
    snoozeShopifyScopesDialog: (appSettings) => {
      appSettings.snoozeShopifyScopesDialog = Math.round(
        DateTime.now().plus({ days: 2 }).toSeconds()
      );
    },
    clearSnoozeShopifyScopesDialog: (appSettings) => {
      appSettings.snoozeShopifyScopesDialog = undefined;
    },
    dismissAlert: (
      appSettings,
      value: PayloadAction<{ orgId: string; alertId: string }>
    ) => {
      if (!(value.payload.orgId in (appSettings.dismissedAlerts || {}))) {
        appSettings.dismissedAlerts[value.payload.orgId] = {};
      }
      if (
        !(
          value.payload.alertId in
          (appSettings.dismissedAlerts[value.payload.orgId] || {})
        )
      ) {
        appSettings.dismissedAlerts[value.payload.orgId][
          value.payload.alertId
        ] = true;
      }
    },
    appendNewOrg: (appSettings, value: PayloadAction<LoggedOrg>) => {
      const exists = appSettings.loggedOrgs.some(
        (o: any) => o.shopId === value.payload.shopId
      );
      if (!exists) {
        appSettings.loggedOrgs.push({
          shopId: value.payload.shopId,
          name: value.payload.name,
        });
      }
    },
    setPerformanceView: (
      appSettings,
      value: PayloadAction<{ index: number; value: PerformanceView }>
    ) => {
      appSettings.performanceViews[value.payload.index] = value.payload.value;
    },
    addPerformanceView: (appSettings) => {
      appSettings.performanceViews.push({ dataKey: "TBT" });
    },
    removePerformanceView: (
      appSettings,
      value: PayloadAction<{ index: number }>
    ) => {
      if (appSettings.performanceViews.length > 1) {
        appSettings.performanceViews.splice(value.payload.index, 1);
      }
    },
    setUseAppPadding: (appSettings, value: PayloadAction<boolean>) => {
      appSettings.appPadding = value.payload;
    },
    setSideNavCollapsed: (appSettings) => {
      appSettings.sideNavCollapsed = !appSettings.sideNavCollapsed;
      return appSettings;
    },
    initSideNavCollapsed: (appSettings, value: PayloadAction<boolean>) => {
      appSettings.sideNavCollapsed = value.payload;
      return appSettings;
    },
    resetExperienceListFilters: (appSettings) => {
      appSettings.experienceListFilters = initialExperienceListFilters;
      return appSettings;
    },
    resetPersonalizationListFilters: (appSettings) => {
      appSettings.personalizationListFilters =
        initialPersonalizationListFilters;
      return appSettings;
    },
    setListFilter: (
      appSettings,
      action: PayloadAction<{
        type: "personalizationListFilters" | "experienceListFilters";
        key: keyof ExperienceListFiltersType;
        value: any;
      }>
    ) => {
      appSettings[action.payload.type][action.payload.key] =
        action.payload.value;
      return appSettings;
    },
  },
  selectors: {
    selectedViewAsMode: (state) => state.viewAsMode,
    selectedViewAsProductId: (state) => state.viewAsProductId,
    selectedExperienceListFilters: (state) => state.experienceListFilters,
    selectedPersonalizationListFilters: (state) =>
      state.personalizationListFilters,
    selectedShowDuplicateProductIntro: (state) =>
      state.showDuplicateProductIntro,
    selectedRememberEmail: (state) => state.rememberEmail,
    selectedIsShopLogin: (state) => state.isShopLogin,
    selectedPerformanceViews: (state) => state.performanceViews,
    selectedSideNavCollapsed: (state) => state.sideNavCollapsed,
  },
});

export const {
  setViewAsMode,
  setViewAsProductId,
  setEmailLogin,
  setShopLogin,
  appendNewOrg,
  rememberEmail,
  dismissAlert,
  clearRememberedEmail,
  disabledDuplicateProductIntro,
  setPerformanceView,
  addPerformanceView,
  removePerformanceView,
  clearSnoozeShopifyScopesDialog,
  snoozeShopifyScopesDialog,
  setUseAppPadding,
  setSideNavCollapsed,
  initSideNavCollapsed,
  resetPersonalizationListFilters,
  resetExperienceListFilters,
  setListFilter,
} = appSettingsSlice.actions;

export const localSettingsReducer = appSettingsSlice.reducer;

export const getIsAlertDismissed =
  (orgId?: string, alertId?: string) => (state: RootState) => {
    if (!alertId || !orgId || !state.appSettings.dismissedAlerts) {
      return false;
    }

    if (!(orgId in state.appSettings.dismissedAlerts)) {
      return false;
    }

    return alertId in state.appSettings.dismissedAlerts[orgId];
  };

export const {
  selectedViewAsMode,
  selectedViewAsProductId,
  selectedExperienceListFilters,
  selectedPersonalizationListFilters,
  selectedRememberEmail,
  selectedIsShopLogin,
  selectedPerformanceViews,
} = appSettingsSlice.selectors;

export const getNeedsWideApp = (state: RootState) =>
  state.variations.variations.length > 6;
